import { getCurrencyDetails } from '../../utils/common';
import CurrencyTemplate from '../components/currencyTemplate';
import IntroPricingSubscriptionAmount from '../components/introPricingSubscriptionAmount';
import LocaleKeys from '../../locales/keys';
import { getNextPaymentDateText } from '../helpers/subscriptionBasedProductCodeTables';
import { stripePaymentTypes } from '../components/paymentMethodStripe/paymentTypes.constant';
import { ELIGIBILITY_ACTION_NO_UPGRADE } from '../../constants/upgradeJourney.constant';

const getFreeTrialSubscriptionText = (
  subscriptionUiNode,
  pricingPlanCurrency,
) => {
  const subscriptionFreeTrial = getCurrencyDetails(
    subscriptionUiNode?.details?.Items[0]?.PricingPlan?.ChargeAmount,
    pricingPlanCurrency,
  );
  const subscriptionFreeTrialHasDiscount = getCurrencyDetails(
    subscriptionUiNode?.details?.Items[0]?.PricingPlan?.RenewalChargeAmount,
    pricingPlanCurrency,
  );
  const hasDiscount = !!subscriptionUiNode?.details?.Items[0]?.Discounts;
  return hasDiscount
    ? subscriptionFreeTrialHasDiscount.priceWithCurrencySymbol
    : subscriptionFreeTrial.priceWithCurrencySymbol;
};

const setIntroPricingTemplate = ({
  subscriptionUiNode,
  pricingPlanCurrency,
  introPricingNode,
  isFreeTrialNode,
}) => {
  if (introPricingNode?.isIntroPricing) {
    return <IntroPricingSubscriptionAmount introPricing={introPricingNode} />;
  }

  if (isFreeTrialNode) {
    return getFreeTrialSubscriptionText(
      subscriptionUiNode,
      pricingPlanCurrency,
    );
  }

  const subscriptionAmountWithCurrency = getCurrencyDetails(
    subscriptionUiNode?.details?.RenewAmount,
    pricingPlanCurrency,
  );

  return subscriptionAmountWithCurrency?.price ? (
    <CurrencyTemplate
      prefixCurrencySymbol={subscriptionAmountWithCurrency.prefixCurrencySymbol}
      discountPriceCurrencySymbol={
        subscriptionAmountWithCurrency.currencySymbol
      }
      price={subscriptionAmountWithCurrency.price}
    />
  ) : (
    ''
  );
};

const platformBasedOnPricingPlanName = (subscriptionUiItem) => {
  const getAppPlatforms = {
    amazon: 'Amazon',
    google: 'Android',
    itunes: 'iOS',
    roku: 'Roku',
  };
  const platFormList = ['amazon', 'google', 'itunes', 'roku'];
  const findPlatformText = platFormList.find((platformText) =>
    (subscriptionUiItem?.Items?.[0]?.PricingPlan?.Name || '')
      .toLowerCase()
      .includes(platformText),
  );
  return findPlatformText ? getAppPlatforms[findPlatformText] : 'Web';
};

const findEntitlePlans = (subscriptionUiItemId, entitledPlanList) =>
  entitledPlanList.find(
    (entitledPlanItem) =>
      subscriptionUiItemId === Number(entitledPlanItem?.subscriptionId),
  );

export const getPlatformName = (subscriptionUiItem, entitledPlans = []) => {
  const getEntitledPlan = findEntitlePlans(
    subscriptionUiItem?.Id,
    entitledPlans,
  );
  if (getEntitledPlan) {
    switch (getEntitledPlan?.appSource.toLowerCase()) {
      case 'ioscore':
        return 'F1 App on Apple';
      case 'iostv':
        return 'F1 TV app on Apple';
      case 'androidcore':
        return 'F1 App on Android';
      case 'android':
        return 'F1 TV App on Android';
      case 'roku':
        return 'Roku';
      case 'amazon':
        return 'Amazon Fire';
      case 'web':
        return 'web';
      default:
        return getEntitledPlan?.appSource;
    }
  }
  return platformBasedOnPricingPlanName(subscriptionUiItem);
};

export const getEligibilityAction = (
  subscriptionUiItem,
  entitledPlans = [],
) => {
  const getEntitledPlan = findEntitlePlans(
    subscriptionUiItem?.Id,
    entitledPlans,
  );

  return getEntitledPlan?.eligibilityAction || ELIGIBILITY_ACTION_NO_UPGRADE;
};

const setPricingTextTemplate = (
  pricingText,
  isFreeTrialNode,
  transformText,
  t,
) => {
  if (isFreeTrialNode) {
    return `${t(pricingText)}${transformText}`;
  }
  return `${t(pricingText)}`;
};

const setPaymentDataTemplate = (isFreeTrialNode) => {
  if (isFreeTrialNode) {
    return LocaleKeys.subscriptionInfo.subscriptions
      .subscriptionTrialItemNextPaymentDateLabel;
  }
  return LocaleKeys.subscriptionInfo.subscriptions.subscriptionItemRenewalDate;
};

const setPlatformTemplate = () =>
  LocaleKeys.subscriptionInfo.subscriptions.subscriptionPlatformLabel;

export const getSubscriptionListModel = (
  subscriptionUiNode,
  pricingPlanCurrency,
  subscriptionPropertiesUiNode,
  introPricingNode = {},
  isFreeTrialNode = false,
  t,
) => {
  const currentSubscriptionDetails = [
    {
      name: setPricingTextTemplate(
        setPlatformTemplate(),
        isFreeTrialNode,
        '**',
        t,
      ),
      value: subscriptionPropertiesUiNode?.platformName,
      id: 'platform',
    },
    {
      name: setPricingTextTemplate(
        LocaleKeys.subscriptionInfo.subscriptions.subscriptionAmountLabel,
        isFreeTrialNode,
        '*',
        t,
      ),
      value: setIntroPricingTemplate({
        subscriptionUiNode,
        pricingPlanCurrency,
        introPricingNode,
        isFreeTrialNode,
      }),
      id: 'subscriptionAmount',
    },
    {
      name: setPricingTextTemplate(
        setPaymentDataTemplate(isFreeTrialNode),
        isFreeTrialNode,
        '**',
        t,
      ),
      value: getNextPaymentDateText(
        subscriptionPropertiesUiNode,
        subscriptionUiNode,
      ),
      id: 'nextPaymentData',
    },
  ];
  if (subscriptionPropertiesUiNode.isIap) {
    return currentSubscriptionDetails.filter(
      (item) => item.id !== 'subscriptionAmount',
    );
  }
  return currentSubscriptionDetails;
};

export const getPaymentListModel = (subscriptionUiNode, subscriptionIdNode) => {
  if (subscriptionUiNode?.details?.PaymentInstrument?.StripeAccount) {
    const nameOnCard =
      subscriptionUiNode?.details?.PaymentInstrument?.BillingAddress?.Name;
    const accountNumber =
      subscriptionUiNode?.details?.PaymentInstrument?.StripeAccount
        ?.CardAccountNumberMasked;
    const splitExpirationMonthAndDate =
      subscriptionUiNode?.details?.PaymentInstrument?.StripeAccount?.CardExpirationDate?.split(
        '/',
      );
    const twoDigitExpMonth =
      splitExpirationMonthAndDate?.[0]?.length === 1
        ? `0${splitExpirationMonthAndDate?.[0]}`
        : splitExpirationMonthAndDate?.[0];
    const cardBrand =
      subscriptionUiNode?.details?.PaymentInstrument?.StripeAccount
        ?.PaymentMethodSubType;
    const paymentMethodId =
      subscriptionUiNode?.details?.PaymentInstrument?.StripeAccount
        ?.PaymentMethodId;
    const paymentMethodType =
      subscriptionUiNode?.details?.PaymentInstrument?.StripeAccount
        ?.PaymentMethodType;

    return [
      {
        NameOnCard: nameOnCard,
        AccountNumber: accountNumber,
        ExpirationMonth: twoDigitExpMonth || '--',
        ExpirationYear: splitExpirationMonthAndDate?.[1] || '--',
        Default: subscriptionUiNode?.details?.PaymentInstrument?.Default,
        Type: cardBrand,
        Id: subscriptionIdNode,
        PaymentInstrumentId: subscriptionUiNode?.details?.PaymentInstrument?.Id,
        BillingAddress: {
          ...subscriptionUiNode?.details?.PaymentInstrument?.BillingAddress,
        },
        PaymentMethodType: paymentMethodType,
        PaymentMethodId: paymentMethodId,
      },
    ];
  }
  return [
    {
      ...subscriptionUiNode?.details?.PaymentInstrument?.CreditCard,
      Default: subscriptionUiNode?.details?.PaymentInstrument?.Default,
      Id: subscriptionIdNode,
      PaymentInstrumentId: subscriptionUiNode?.details?.PaymentInstrument?.Id,
      BillingAddress: {
        ...subscriptionUiNode?.details?.PaymentInstrument?.BillingAddress,
      },
      Name: subscriptionUiNode?.details?.PaymentInstrument?.Name,
      PaymentMethodType: stripePaymentTypes.CARD,
    },
  ];
};

export const getPaymentInstrumentList = (
  retrievePaymentInstrument,
  subscriptionIdNode,
  removePaymentInstrumentStatus,
) =>
  (retrievePaymentInstrument || []).reduce((all, item) => {
    if (item?.CreditCard && !removePaymentInstrumentStatus) {
      const getCyberSourcePayment = getPaymentListModel(
        { details: { PaymentInstrument: { ...item } } },
        subscriptionIdNode,
      );
      const [cyberSourcePayment] = getCyberSourcePayment;
      all.push({
        ...cyberSourcePayment,
      });
    } else if (item?.StripeAccount && item?.TypeName.includes('Stripe')) {
      const getStipePayment = getPaymentListModel(
        { details: { PaymentInstrument: { ...item } } },
        subscriptionIdNode,
      );
      const [stripePaymentEntity] = getStipePayment;
      all.push({
        ...stripePaymentEntity,
      });
    } else {
      all.push({
        ...item,
        Default: item?.Default,
        PaymentInstrumentId: item?.PaymentInstrumentId,
        Id: subscriptionIdNode,
        BillingAddress: { ...item?.BillingAddress },
        Name: item?.Name,
        PaymentMethodType: stripePaymentTypes.CARD,
      });
    }
    return all;
  }, []);

export const getProductIdFromSubscription = (subscriptionUiModelNode) => {
  const { details } = subscriptionUiModelNode;
  const { Items } = details || {};
  const getFirstElement = (Items || []).slice(0, 1);
  const getProductItems = getFirstElement.reduce(
    (all, item) => {
      const { Id } = item?.Product;
      const { PricingPlan } = item;
      all = {
        Id: Id,
        isProductId: !!Id,
        PricingPlan,
      };
      return all;
    },
    { isProductId: false },
  );
  return getProductItems;
};

export const getUiModelAddresslist = (addressListNodes) =>
  (addressListNodes || []).reduce((all, item) => {
    const displayName = `${item?.LineOne || ''} ${item?.City || ''}, ${
      item?.State || ''
    } ${item?.PostalCode || ''}`;
    all.push({ ...item, displayName });
    return all;
  }, []);

export const isSequenceUpdateFormNode = (initValues, updatedValues) => {
  const getUpdatedValues = updatedValues || {};
  const getUpdateKeyValues = Object.entries(getUpdatedValues);
  const getUpdatedNodes = getUpdateKeyValues.reduce((all, item) => {
    const [key, value] = item;
    if (initValues[key] !== value) {
      all = true;
    }
    return all;
  }, false);
  return getUpdatedNodes;
};

export const setRetrievePaymentInstrumentNode = (
  paymentInstrumentMethodList,
  paymentInstrumentId,
  cardDetails,
  billingAddress,
) =>
  (paymentInstrumentMethodList || []).reduce((all, item) => {
    if (item.Id === paymentInstrumentId) {
      all.push({
        ...item,
        BillingAddress: billingAddress,
        CreditCard: { ...item.CreditCard, ...cardDetails },
      });
    } else {
      all.push(item);
    }
    return all;
  }, []);

export const setRetrievePaymentInstrumentStripeNode = (
  paymentInstrumentMethodList,
  paymentInstrumentId,
  stripeAccount,
  billingAddress,
) =>
  (paymentInstrumentMethodList || []).reduce((all, item) => {
    if (item.Id === paymentInstrumentId) {
      all.push({
        ...item,
        StripeAccount: stripeAccount,
        BillingAddress: billingAddress,
      });
    } else {
      all.push(item);
    }
    return all;
  }, []);

export const setCardToStripeRetrievePaymentInstrumentStripeNode = (
  paymentInstrumentMethodList,
  paymentInstrumentId,
  stripeAccount,
  billingAddress,
  updatedPaymentInstrumentNode,
) =>
  (paymentInstrumentMethodList || []).reduce((all, item) => {
    if (item.Id === paymentInstrumentId) {
      all.push({
        ...updatedPaymentInstrumentNode,
        StripeAccount: stripeAccount,
        BillingAddress: billingAddress,
      });
    } else {
      all.push(item);
    }
    return all;
  }, []);

export const setPaymentMethodNode = (
  paymentMethodListNode,
  paymentInstrumentId,
  formikValues,
  addressListNode,
  updateCreditCardDetails,
) => {
  const { BillingAddress, ExpirationDate, NameOnCard } = formikValues;
  const getBillingAddressBasedonId = (addresslist, addressId) => {
    const getAddressNode = addresslist.find(
      (item) => item.Id?.toString() === addressId?.toString(),
    );
    const { displayName, ...restAddressNode } = getAddressNode;
    return restAddressNode;
  };
  const getPaymethodList = paymentMethodListNode.reduce(
    (all, item) => {
      if (item.PaymentInstrumentId === paymentInstrumentId) {
        const cardDetails = {
          NameOnCard: NameOnCard,
          ExpirationMonth: ExpirationDate.split('/')[0],
          ExpirationYear: `20${ExpirationDate.split('/')[1]}`,
          AccountNumber: updateCreditCardDetails?.AccountNumber,
          Type: updateCreditCardDetails?.Type,
        };
        const billAddressValues = getBillingAddressBasedonId(
          addressListNode,
          BillingAddress,
        );
        all.paymentMethodListValues.push({
          ...item,
          ...cardDetails,
          BillingAddress: billAddressValues,
        });
        all.creditCardDetails = { ...cardDetails };
        all.billngAddressDetails = { ...billAddressValues };
      } else {
        all.paymentMethodListValues.push(item);
      }
      return all;
    },
    {
      paymentMethodListValues: [],
      creditCardDetails: null,
      billngAddressDetails: null,
    },
  );
  return getPaymethodList;
};

export const getPricingPlan = (pricingPlansNode, productPricingPlanUiNode) =>
  (productPricingPlanUiNode || []).find(
    (item) => item.Id === pricingPlansNode?.Id,
  );

export const getIsFreeTrialNode = (getPricingPlanNode, referenceNode) => {
  if (!getPricingPlanNode?.AdditionalProperties) {
    return false;
  }
  const freeTrialNode = (getPricingPlanNode?.AdditionalProperties).reduce(
    (all, item) => {
      const getExternalReference = item.ExternalReference;
      if (getExternalReference === referenceNode) {
        all = item?.Values[0]?.toLowerCase() === 'true' ? true : false;
      }
      return all;
    },
    false,
  );
  return freeTrialNode;
};

export const getAddNewAddressAddressList = (billingAddress, addressList) => {
  const getAddressList = getUiModelAddresslist([billingAddress]);
  const addressListWithoutAddNewAddressItem = addressList.filter(
    (addressItem) => addressItem.Id !== 0,
  );

  return { addressListWithoutAddNewAddressItem, getAddressList };
};

export const getSubscriptionListDetails = (subscriptionUiModelDetails) => {
  if (!subscriptionUiModelDetails || !subscriptionUiModelDetails.length) {
    return {};
  }
  const getSubscriptionIdBasedOnAccountNumber =
    subscriptionUiModelDetails.reduce((all, item) => {
      const isAccountNumber =
        item?.PaymentInstrument?.CreditCard?.AccountNumber ||
        item?.PaymentInstrument?.StripeAccount?.PaymentMethodId;

      if (isAccountNumber) {
        all[item.Id] = true;
      } else {
        all[item.Id] = false;
      }
      return all;
    }, {});
  const fetchBasedOnAccountNumber = Object.values(
    getSubscriptionIdBasedOnAccountNumber,
  ).every((item) => !item);
  const fetchWebApiNode = fetchBasedOnAccountNumber
    ? { [subscriptionUiModelDetails?.[0]?.Id]: true }
    : getSubscriptionIdBasedOnAccountNumber;
  return fetchWebApiNode;
};
